import Box from '@mui/material/Box';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { ReactNode, useMemo } from 'react';

import { BillingAmount, BillingSummary } from '~hooks/useBilling/models';
import { makeCurrencyFormatter } from '~hooks/useCompanyCurrency/useCompanyCurrency';

const labelSx = (theme: Theme) => ({
  color: theme.brandV2.colors.treadGray4,
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
});

const valueSx = (theme: Theme) => ({
  color: theme.brandV2.colors.treadBlack,
  fontSize: '18px',
  fontVariantNumeric: 'tabular-nums',
  fontWeight: 600,
  lineHeight: 1.3,
});

interface AmountListProps {
  amounts: BillingAmount[];
}

// Totals are never combined across currencies — one line per currency entry.
function AmountList({ amounts }: AmountListProps) {
  const formatZero = useMemo(() => makeCurrencyFormatter(), []);
  const formatters = useMemo(() => {
    const formattersByCurrency = new Map<
      string,
      ReturnType<typeof makeCurrencyFormatter>
    >();
    amounts.forEach((entry) => {
      if (!formattersByCurrency.has(entry.currency)) {
        formattersByCurrency.set(entry.currency, makeCurrencyFormatter(entry.currency));
      }
    });

    return formattersByCurrency;
  }, [amounts]);

  if (amounts.length === 0) {
    return <Typography sx={valueSx}>{formatZero(0)}</Typography>;
  }

  return (
    <>
      {amounts.map((entry) => (
        <Typography key={entry.currency} sx={valueSx}>
          {formatters.get(entry.currency)?.(entry.amount)}
          <Box
            component="span"
            sx={(theme) => ({
              color: theme.brandV2.colors.treadGray4,
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.08em',
              ml: 0.5,
              textTransform: 'uppercase',
            })}
          >
            {entry.currency}
          </Box>
        </Typography>
      ))}
    </>
  );
}

interface StatProps {
  label: string;
  value: ReactNode;
}

function Stat({ label, value }: StatProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, px: 2, py: 1.5 }}>
      <Typography sx={labelSx}>{label}</Typography>
      {value}
    </Box>
  );
}

interface BillingSummaryStripProps {
  summary: BillingSummary;
}

export default function BillingSummaryStrip({ summary }: BillingSummaryStripProps) {
  return (
    <Box data-test-id="settings-billing-summary-strip">
      <Box
        sx={(theme) => ({
          bgcolor: theme.brandV2.colors.treadGray9,
          border: `1px solid ${theme.brandV2.colors.treadGray7}`,
          borderRadius: theme.spacing(),
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          '& > :not(:first-of-type)': {
            borderLeft: { sm: `1px solid ${theme.brandV2.colors.treadGray7}` },
            borderTop: {
              xs: `1px solid ${theme.brandV2.colors.treadGray7}`,
              sm: 'none',
            },
          },
        })}
      >
        <Stat
          label={t('billing.tab.amount_due')}
          value={<AmountList amounts={summary.amountsDue} />}
        />
        <Stat
          label={t('billing.tab.next_payment_due')}
          value={
            <Typography sx={valueSx}>
              {/* next_due_date is a calendar date (no time); a dayjs.tz() conversion
                  can shift the displayed day off the actual due date */}
              {summary.nextDueDate
                ? summary.nextDueDate.format('MMM D, YYYY')
                : t('billing.tab.nothing_due')}
            </Typography>
          }
        />
        <Stat
          label={t('billing.tab.paid_this_year')}
          value={<AmountList amounts={summary.paidYtd} />}
        />
      </Box>
      {(summary.openInvoicesTruncated || summary.paidYtdTruncated) && (
        <Typography
          sx={(theme) => ({
            color: theme.semantic.textSecondary,
            fontSize: 11,
            mt: 0.5,
          })}
        >
          {t('billing.tab.summary_truncated')}
        </Typography>
      )}
    </Box>
  );
}
